/**
 * ChangePassword — authenticated password-change form.
 *
 * Displayed inside the `PageModal` slide-over panel.
 * Validates the new password against strength rules (lowercase, uppercase,
 * digit, special char, min 8 chars) and enforces confirm-match before
 * enabling the submit button.
 */
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { HiOutlineEye, HiOutlineEyeOff, HiOutlineX, HiOutlineCheck } from "react-icons/hi";
import { HiOutlineLockClosed, HiOutlineShieldCheck } from "react-icons/hi2";

import { changePassword } from "@service/change-password-handler";
import { getColorTheme } from "@service/local-storage-handler";

import "./ChangePassword.scss";

const ChangePassword = () => {
  const themeFromStore = useSelector(({ theme }) => theme.theme);
  const colorTheme = themeFromStore || getColorTheme() || "dark";

  const [validations, setValidations] = useState({
    hasLowercase: false,
    hasUppercase: false,
    hasDigit: false,
    hasSpecialChar: false,
    meetsLength: false,
  });

  const [errorMsg, setErrorMsg] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrPassword, setShowCurrPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCnfmPassword, setShowCnfmPassword] = useState(false);

  const allValid = Object.values(validations).every(Boolean);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const disableChangePasswordButton = useMemo(() => {
    return !(
      !!oldPassword &&
      !!newPassword &&
      !!confirmPassword &&
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-zA-Z]).{8,}$/.test(
        newPassword
      ) &&
      newPassword === confirmPassword
    );
  }, [oldPassword, newPassword, confirmPassword]);

  const validatePassword = (password: string) => {
    setValidations({
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasDigit: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*]/.test(password),
      meetsLength: password.length >= 8,
    });
  };

  const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(e.target.value);
    setErrorMsg("");
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    validatePassword(value);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);
  };

  const handleChangePassword = () => {
    changePassword(oldPassword, newPassword)
      .then(() => {
        setErrorMsg("");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        toast.success(
          (t) => (
            <div className={`toast-msgs-container ${colorTheme}`}>
              <label
                style={{ color: colorTheme === "dark" ? "#f7fafc" : "#1a202c" }}
              >
                Password changed successfully
              </label>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="dismiss-btn"
              >
                Dismiss
              </button>
            </div>
          ),
          { duration: 6000 }
        );
      })
      .catch((error) => {
        toast.error(
          (t) => (
            <div className={`toast-msgs-container ${colorTheme}`}>
              <label
                style={{ color: colorTheme === "dark" ? "#f7fafc" : "#1a202c" }}
              >
                Please enter valid password
              </label>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="dismiss-btn"
              >
                Dismiss
              </button>
            </div>
          ),
          { duration: 6000 }
        );

        if (error.response?.data?.error === "Incorrect Pasword") {
          setErrorMsg(error?.response?.data?.error || error?.message || "Password change failed");
        }
      });
  };

  /* Strength meter: count how many rules pass → 0..5 */
  const strengthCount = Object.values(validations).filter(Boolean).length;
  const strengthPercent = (strengthCount / 5) * 100;
  const strengthLabel =
    strengthCount <= 1 ? "Weak" : strengthCount <= 3 ? "Fair" : strengthCount <= 4 ? "Good" : "Strong";

  return (
    <div className={`change-password ${colorTheme}`}>
      <div className="cp-inner">
        {/* ── Left: Form ── */}
        <div className="cp-form-col">
          <div className="cp-header">
            <div className="cp-icon-wrap">
              <HiOutlineLockClosed size={20} />
            </div>
            <div>
              <h2 className="cp-title">Change Password</h2>
              <p className="cp-sub">Secure your account with a new password</p>
            </div>
          </div>

          <div className="cp-fields">
            {/* Current */}
            <div className="cp-field">
              <label>Current password</label>
              <div className="cp-input-wrap">
                <input
                  type={showCurrPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  value={oldPassword}
                  onChange={handleOldPasswordChange}
                  className={errorMsg ? "has-error" : ""}
                />
                <button
                  type="button"
                  className="cp-eye"
                  onClick={() => setShowCurrPassword(!showCurrPassword)}
                >
                  {showCurrPassword ? <HiOutlineEye size={18} /> : <HiOutlineEyeOff size={18} />}
                </button>
              </div>
              {errorMsg && (
                <span className="cp-error"><HiOutlineX size={14} /> {errorMsg}</span>
              )}
            </div>

            {/* New */}
            <div className="cp-field">
              <label>New password</label>
              <div className="cp-input-wrap">
                <input
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                />
                <button
                  type="button"
                  className="cp-eye"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <HiOutlineEye size={18} /> : <HiOutlineEyeOff size={18} />}
                </button>
              </div>

              {/* Inline strength bar */}
              {newPassword && (
                <div className="cp-strength">
                  <div className="cp-strength-track">
                    <div
                      className={`cp-strength-fill s-${strengthCount}`}
                      style={{ width: `${strengthPercent}%` }}
                    />
                  </div>
                  <span className={`cp-strength-label s-${strengthCount}`}>{strengthLabel}</span>
                </div>
              )}
            </div>

            {/* Confirm */}
            <div className="cp-field">
              <label>Confirm new password</label>
              <div className="cp-input-wrap">
                <input
                  type={showCnfmPassword ? "text" : "password"}
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  className={confirmPassword ? (passwordsMatch ? "is-valid" : "is-mismatch") : ""}
                />
                <button
                  type="button"
                  className="cp-eye"
                  onClick={() => setShowCnfmPassword(!showCnfmPassword)}
                >
                  {showCnfmPassword ? <HiOutlineEye size={18} /> : <HiOutlineEyeOff size={18} />}
                </button>
              </div>
              {confirmPassword && (
                <span className={`cp-match ${passwordsMatch ? "ok" : "no"}`}>
                  {passwordsMatch
                    ? <><HiOutlineCheck size={14} /> Passwords match</>
                    : <><HiOutlineX size={14} /> Passwords don't match</>}
                </span>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="cp-submit"
            onClick={handleChangePassword}
            disabled={disableChangePasswordButton}
          >
            <HiOutlineShieldCheck size={18} />
            Update Password
          </button>
        </div>

        {/* ── Right: Requirements checklist ── */}
        <div className="cp-reqs-col">
          <div className="cp-reqs-card">
            <h3 className="cp-reqs-title">Password Rules</h3>
            <ul className="cp-reqs-list">
              {[
                { key: "meetsLength", text: "At least 8 characters" },
                { key: "hasUppercase", text: "Uppercase letter (A-Z)" },
                { key: "hasLowercase", text: "Lowercase letter (a-z)" },
                { key: "hasDigit", text: "Number (0-9)" },
                { key: "hasSpecialChar", text: "Special char (!@#$%^&*)" },
              ].map((r) => (
                <li key={r.key} className={validations[r.key as keyof typeof validations] ? "pass" : ""}>
                  <span className="cp-req-icon">
                    {validations[r.key as keyof typeof validations]
                      ? <HiOutlineCheck size={14} />
                      : <span className="cp-req-dot" />}
                  </span>
                  {r.text}
                </li>
              ))}
            </ul>
            {allValid && (
              <div className="cp-reqs-ok">
                <HiOutlineShieldCheck size={16} /> All requirements met
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
